import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import CreditCard from "../CreditCard";
import cn from "../../lib/classnames";
import DateOfBirthInput from "../DateOfBirthInput";
import ExpandableAddress from "../ExpandableAddress";
import ExpandableCollege from "./ExpandableCollege";
import ExpandableName from "../ExpandableName";
import ExpandingCheckbox from "../ExpandingCheckbox";
import getFullName from "../../lib/getFullName";
import LinedInput, {InputType} from "../LinedInput";
import Modal from "../Modal";
import MoneyInput from "../MoneyInput";
import PhoneInput from "../PhoneInput";
import React, {useState, useEffect, useRef, createRef} from "react";
import Select from "../Select";
import SocialSecurityInput from "../SocialSecurityInput";
import styles from "./ApplicationForm.module.css";
import TextLink from "../TextLink";
import useFocusInvalid from "../../lib/useFocusInvalid";
import ValidationList from "../ValidationList";
import {
    AddressKeys,
    AppKeys,
    Application,
    AppValidation,
    AddressValidationKeys,
} from "../../lib/api/models";
import {cardCategories, GOLD_CARD_VISA_INDEX, GOLD_CARD_MC_INDEX} from "../CardPicker";
import {Column, Container, Row} from "../grid";

const strings = {
    title: "1st Financial Bank USA Credit Card",
    accountTotal: "Total in Accounts",
    accountTotalExtra:
        "Enter your estimate of the total amount in your checking and savings accounts.",
    annualGrossIncome: "Annual Gross Income",
    annualGrossIncomeExtra:
        "You may include your independent income including full-time, part-time, or seasonal jobs, self-employment, tips, gifts, grants, and public assistance. You may also include money from somebody else that is regularly deposited into your individual account or into a joint account that person shares with you. If you are 21 or over, you may also include somebody else's income that is regularly used to pay your expenses. " +
        "Alimony, child support or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repayment.",
    bankAccount: "Bank Accounts Owned",
    continue: "Continue",
    dateOfBirth: "Date of Birth (MM/DD/YYYY)",
    dobExtra:
        "We use this to verify your identity as required by Federal law. You must be at least 18 years old (or older in certain states) to be eligible for a credit card account.",
    email: "Email",
    emailExtra:
        "We may use your email address to send you important information about your application and account(s), as well as other useful products and services.",
    grossIncomeModal: "Gross Income Modal",
    grossIncomeModalText:
        "Be sure to include all sources of income. Expect to work part time during the school year, get an internship, or work a summer job? If you’re under 21, include money regularly provided by your parents or others. If you are 21 or over, you may also include somebody else’s income that is regularly used to pay your expenses.",
    monthlyObligation: "Monthly Obligations",
    monthlyObligationsModal: "Monthly obligations Modal",
    monthlyObligationsModalText:
        "You have indicated a monthly housing payment but provided $0 as the amount. We have removed your selection.",
    monthlyPayment: "I am obligated to pay a mortgage, rent, etc.",
    monthlyPaymentExtra:
        "Enter the amount you pay each month for a mortgage, rent and/or other obligations. If a parent or someone else pays for your housing and/or other obligations, do not include those amounts. If you have roommates, only include your share of the rent.",
    none: "None",
    phoneExtra:
        "By providing your phone number, you consent to our use of automatic dialers, text or pre-recorded or artificial voice messages to service your account. Message and data rates may apply.",
    primaryPhone: "Primary Phone",
    paymentAccount: "Payment Account",
    paymentExtra: "Account you plan to use to pay your credit card bill.",
    residency: "US Citizen or Permanent Resident",
    socialSecurity: "Social Security Number/ITIN",
    ssExtra: "We use this to verify your identity as required by Federal law.",
    goldCard: "Gold Card",
    yourNameHere: "Your Name Here",
    checking: "Checking",
    savings: "Savings",
    bothCheckingSavings: "Both Checking and Savings",
    yes: "Yes",
    no: "No",
    ratesAndOtherInfo: "See Rates and Other Info",
    other: "Other",
};

const EMPTY_APP_VALIDATION: AppValidation = {};

enum BankAccounts {
    None = "none",
    Checking = "checking",
    Savings = "savings",
    Both = "checking_savings",
    Other = "other",
}
const bankAccountsArr = [
    BankAccounts.None,
    BankAccounts.Checking,
    BankAccounts.Savings,
    BankAccounts.Both,
];
const bankAccountStrings = bankAccountsArr.map((accountType) => {
    if (accountType === BankAccounts.None) {
        return strings.none;
    }
    if (accountType === BankAccounts.Checking) {
        return strings.checking;
    }
    if (accountType === BankAccounts.Savings) {
        return strings.savings;
    }
    return strings.bothCheckingSavings;
});

const payAccountStrings = [strings.checking, strings.savings, strings.other];
const payAccountValues = [BankAccounts.Checking, BankAccounts.Savings, BankAccounts.Other];

enum BoolDecision {
    No,
    Yes,
}
const boolDecisionArr = [BoolDecision.Yes, BoolDecision.No];
const boolDecisionStrings = [strings.yes, strings.no];

function renderCard(
    firstName: string,
    lastName: string,
    defaultCardBrand: "VISA" | "MC",
    singleCardType?: string,
) {
    let cardCategory =
        cardCategories[defaultCardBrand === "VISA" ? GOLD_CARD_VISA_INDEX : GOLD_CARD_MC_INDEX];
    if (singleCardType) {
        const foundCat = cardCategories.find((c) => c.cardType === singleCardType);
        if (foundCat) {
            cardCategory = foundCat;
        }
    }
    const cardName = getFullName(firstName, "", lastName);
    return (
        <div className={styles.goldCard}>
            <CreditCard
                imageAlt={strings.goldCard}
                imageSources={cardCategory.cards[0].images}
                text={cardName.length ? cardName : strings.yourNameHere}
                textColor={cardCategory.textColor}
            />
        </div>
    );
}

type Props = {
    application: Application;
    continueDisabled?: boolean;
    continueLabel: string;
    defaultCardBrand: "VISA" | "MC";
    firstInvalidKey: string | undefined;
    isRatesVisible?: boolean;
    loading: boolean;
    onBlurInput: (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onChangeApplication: (name: AppKeys | AddressKeys, value: string | boolean) => void;
    onContinue: () => void;
    renderAddress?: (expandAddressGroupOnUpdate: () => void) => React.ReactNode;
    renderCardPicker?: () => React.ReactNode;
    renderCollegeInput?: () => React.ReactNode;
    renderAgreement?: () => React.ReactNode;
    renderPaperless?: () => React.ReactNode;
    singleCardType?: string;
    validation: validationLib.Validation;
    validationCount: number;
};

export default function ApplicationForm({
    application,
    continueDisabled,
    continueLabel,
    defaultCardBrand,
    firstInvalidKey,
    isRatesVisible = true,
    loading,
    onBlurInput,
    onChangeApplication,
    onContinue,
    renderAddress,
    renderCardPicker,
    renderCollegeInput,
    renderAgreement,
    renderPaperless,
    singleCardType,
    validation,
    validationCount,
}: Props) {
    const windowUrl = window.location.href;
    const formValidation = validation.errors ?? EMPTY_APP_VALIDATION;
    const checkingValidation =
        formValidation[AppKeys.CheckingAccount] !== undefined
            ? formValidation[AppKeys.CheckingAccount]
            : [];
    const savingsValidation =
        formValidation[AppKeys.SavingsAccount] !== undefined
            ? formValidation[AppKeys.SavingsAccount]
            : [];
    const bankValidation = [
        ...(checkingValidation as string[]),
        ...(savingsValidation as string[]),
    ];

    const continueBtnRef = useRef(null);
    const monthlyhCheckboxRef = useRef(null);
    const selectRefs = useRef<{
        [key: string]: React.RefObject<HTMLSelectElement>;
    }>({
        [AppKeys.StudentStatus]: createRef(),
        [AppKeys.SavingsAccount]: createRef(),
        [AppKeys.PaymentAccount]: createRef(),
        [AppKeys.Residency]: createRef(),
    });

    const inputRefs = useRef<{[key: string]: React.RefObject<HTMLInputElement>}>({
        [AppKeys.Email]: createRef(),
        [AppKeys.College]: createRef(),
        [AppKeys.PrimaryPhone]: createRef(),
        [AppKeys.DateOfBirth]: createRef(),
        [AppKeys.SocialSecurity]: createRef(),
        [AppKeys.AccountTotal]: createRef(),
        [AppKeys.AnnualGrossIncome]: createRef(),
        [AppKeys.AnnualObligation]: createRef(),
    });

    const [isNameExpanded, setIsNameExpanded] = useState(false);
    const [isAddressExpanded, setIsAddressExpanded] = useState(false);

    const [checkbox, setCheckbox] = useState(false);

    const [grossIncomeModal, setGrossIncomeModal] = useState(false);
    const [wasGrossIncomeModalShown, setWasGrossIncomeModalShown] = useState(false);
    function closeGrossIncomeModal() {
        setGrossIncomeModal(false);
        setWasGrossIncomeModalShown(true);
    }
    function openGrossIncomeModal() {
        setGrossIncomeModal(true);
    }

    const [monthlyObligationsModal, setMonthlyObligationsModal] = useState(false);
    const [wasMonthlyObligationsModalShown, setWasMonthlyObligationsModalShown] = useState(false);
    function closeMonthlyObligationsModal() {
        setMonthlyObligationsModal(false);
        setWasMonthlyObligationsModalShown(true);

        onChangeApplication(AppKeys.AnnualObligation, "");
        setCheckbox(false);
    }
    function openMonthlyObligationsModal() {
        setMonthlyObligationsModal(true);
    }

    useEffect(() => {
        inputRefs.current[AppKeys.Email]?.current?.focus();
    }, []);

    const firstInvalidRef = firstInvalidKey
        ? inputRefs.current[firstInvalidKey] || selectRefs.current[firstInvalidKey]
        : undefined;
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    function getBankAcctIndex(): number {
        const ownChecking = application[AppKeys.CheckingAccount];
        const ownSavings = application[AppKeys.SavingsAccount];
        if (ownChecking && ownSavings) {
            return bankAccountsArr.findIndex((v) => v === BankAccounts.Both);
        }
        if (ownChecking) {
            return bankAccountsArr.findIndex((v) => v === BankAccounts.Checking);
        }
        if (ownSavings) {
            return bankAccountsArr.findIndex((v) => v === BankAccounts.Savings);
        }
        if (ownChecking === false && ownSavings === false) {
            return bankAccountsArr.findIndex((v) => v === BankAccounts.None);
        }
        return -1;
    }

    function getResidencyIndex() {
        const value = application[AppKeys.Residency];
        if (value) {
            return 0;
        }
        if (value === false) {
            return 1;
        }
        return -1;
    }

    function expandAddressGroupOnUpdate() {
        setIsAddressExpanded(true);
    }

    function onChangeName(name: AppKeys, value: string) {
        onChangeApplication(name, value);
    }

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (application[AppKeys.AnnualObligation] === "$0") {
            openMonthlyObligationsModal();
            return;
        }

        onContinue();
    }

    function onChangeAddress(name: AddressKeys, value: string) {
        onChangeApplication(name, value);
    }

    return (
        <form onSubmit={onSubmit}>
            <Container className={styles.container}>
                <Row>
                    <Column largeOffset={1}>
                        <h1 className={styles.title}>{strings.title}</h1>
                    </Column>
                </Row>
                <Row>
                    <Column largeOffset={6} mediumOffset={4} large={6} medium={4}>
                        <div className={styles.rightInputColumn}>
                            {renderCardPicker ? null : (
                                <div className={styles.cardContainerMobile}>
                                    {renderCard(
                                        application[AppKeys.FirstName],
                                        application[AppKeys.LastName],
                                        defaultCardBrand,
                                        singleCardType,
                                    )}
                                </div>
                            )}
                            {isRatesVisible ? (
                                <TextLink
                                    className={cn(styles.link)}
                                    onClick={() => {
                                        window.open(
                                            `${process.env.REACT_APP_BASE_PATH}${routes.INTEREST_DISCLOSURE}`,
                                        );
                                        callApplicationLead(
                                            "disclosure",
                                            application[AppKeys.InviteCode],
                                            windowUrl,
                                        );
                                    }}
                                    text={strings.ratesAndOtherInfo}
                                />
                            ) : null}
                            {renderCardPicker ? (
                                <div className={styles.cardContainerMobile}>
                                    {renderCardPicker()}
                                </div>
                            ) : null}
                        </div>
                    </Column>
                </Row>
                <Row>
                    <Column largeOffset={1} large={5} medium={4}>
                        <div
                            className={
                                isRatesVisible
                                    ? styles.leftInputColumn
                                    : styles.leftInputColumnNoRates
                            }
                        >
                            <LinedInput
                                id={AppKeys.Email}
                                name={AppKeys.Email}
                                label={strings.email}
                                ref={inputRefs.current[AppKeys.Email]}
                                type={InputType.Email}
                                onBlur={onBlurInput}
                                value={application[AppKeys.Email]}
                                setValue={(value) => onChangeApplication(AppKeys.Email, value)}
                                extraInfo={strings.emailExtra}
                                required={true}
                                maxLength={40}
                                validationMessage={formValidation[AppKeys.Email]}
                            />

                            <ExpandableName
                                firstName={application[AppKeys.FirstName]}
                                isExpanded={isNameExpanded}
                                lastName={application[AppKeys.LastName]}
                                middleInitial={application[AppKeys.MiddleInitial]}
                                onBlurInput={onBlurInput}
                                onChangeName={onChangeName}
                                setIsExpanded={(isExpanded: boolean) =>
                                    setIsNameExpanded(isExpanded)
                                }
                                firstInvalidKey={firstInvalidKey}
                                validationFirst={formValidation[AppKeys.FirstName]}
                                validationLast={formValidation[AppKeys.LastName]}
                                validationMiddle={formValidation[AppKeys.MiddleInitial]}
                                validationCount={validationCount}
                            />

                            {renderAddress ? (
                                renderAddress(expandAddressGroupOnUpdate)
                            ) : (
                                <ExpandableAddress
                                    line1={application[AppKeys.Address][AddressKeys.AddressLine1]}
                                    line2={application[AppKeys.Address][AddressKeys.AddressLine2]}
                                    city={application[AppKeys.Address][AddressKeys.City]}
                                    state={application[AppKeys.Address][AddressKeys.State]}
                                    zipcode={application[AppKeys.Address][AddressKeys.Zipcode]}
                                    onBlurInput={onBlurInput}
                                    onChangeAddress={onChangeAddress}
                                    isExpanded={isAddressExpanded}
                                    setIsExpanded={(isExpanded: boolean) =>
                                        setIsAddressExpanded(isExpanded)
                                    }
                                    firstInvalidKey={firstInvalidKey}
                                    validationLine1={
                                        formValidation[AddressValidationKeys.AddressLine1]
                                    }
                                    validationLine2={
                                        formValidation[AddressValidationKeys.AddressLine2]
                                    }
                                    validationCity={formValidation[AddressValidationKeys.City]}
                                    validationState={formValidation[AddressValidationKeys.State]}
                                    validationZipcode={
                                        formValidation[AddressValidationKeys.Zipcode]
                                    }
                                    validationCount={validationCount}
                                />
                            )}
                            {renderCollegeInput ? (
                                renderCollegeInput()
                            ) : (
                                <ExpandableCollege
                                    college={application[AppKeys.College]}
                                    firstInvalidKey={firstInvalidKey}
                                    onBlurInput={onBlurInput}
                                    onChangeApplication={onChangeApplication}
                                    studentStatus={application[AppKeys.StudentStatus]}
                                    validationCollege={formValidation[AppKeys.College]}
                                    validationCount={validationCount}
                                    validationStudentStatus={formValidation[AppKeys.StudentStatus]}
                                />
                            )}

                            <PhoneInput
                                id={AppKeys.PrimaryPhone}
                                name={AppKeys.PrimaryPhone}
                                label={strings.primaryPhone}
                                ref={inputRefs.current[AppKeys.PrimaryPhone]}
                                onBlur={onBlurInput}
                                value={application[AppKeys.PrimaryPhone]}
                                setValue={(value) => {
                                    onChangeApplication(AppKeys.PrimaryPhone, value);
                                }}
                                extraInfo={strings.phoneExtra}
                                required={true}
                                validationMessage={formValidation[AppKeys.PrimaryPhone]}
                                inputMode="numeric"
                            />
                            <DateOfBirthInput
                                id={AppKeys.DateOfBirth}
                                name={AppKeys.DateOfBirth}
                                label={strings.dateOfBirth}
                                ref={inputRefs.current[AppKeys.DateOfBirth]}
                                onBlur={onBlurInput}
                                value={application[AppKeys.DateOfBirth]}
                                setValue={(value) => {
                                    onChangeApplication(AppKeys.DateOfBirth, value);
                                }}
                                extraInfo={strings.dobExtra}
                                required={true}
                                validationMessage={formValidation[AppKeys.DateOfBirth]}
                                inputMode="numeric"
                            />
                            <SocialSecurityInput
                                id={AppKeys.SocialSecurity}
                                name={AppKeys.SocialSecurity}
                                label={strings.socialSecurity}
                                ref={inputRefs.current[AppKeys.SocialSecurity]}
                                onBlur={onBlurInput}
                                value={application[AppKeys.SocialSecurity]}
                                setValue={(value) => {
                                    onChangeApplication(AppKeys.SocialSecurity, value);
                                }}
                                extraInfo={strings.ssExtra}
                                required={true}
                                validationMessage={formValidation[AppKeys.SocialSecurity]}
                                inputMode="numeric"
                            />
                            <Select
                                id={AppKeys.SavingsAccount}
                                name={AppKeys.SavingsAccount}
                                label={strings.bankAccount}
                                ref={selectRefs.current[AppKeys.SavingsAccount]}
                                options={bankAccountStrings}
                                onBlur={onBlurInput}
                                selectedIndex={getBankAcctIndex()}
                                setSelected={(index) => {
                                    const value = bankAccountsArr[index];
                                    const ownChecking =
                                        value === BankAccounts.Checking ||
                                        value === BankAccounts.Both;
                                    const ownSavings =
                                        value === BankAccounts.Savings ||
                                        value === BankAccounts.Both;
                                    onChangeApplication(AppKeys.CheckingAccount, ownChecking);
                                    onChangeApplication(AppKeys.SavingsAccount, ownSavings);
                                }}
                                required={true}
                                validationMessage={bankValidation}
                            />
                            <Select
                                id={AppKeys.PaymentAccount}
                                name={AppKeys.PaymentAccount}
                                label={strings.paymentAccount}
                                ref={selectRefs.current[AppKeys.PaymentAccount]}
                                options={payAccountStrings}
                                selectedIndex={payAccountValues.findIndex(
                                    (v) => v === application[AppKeys.PaymentAccount],
                                )}
                                setSelected={(index) => {
                                    onChangeApplication(
                                        AppKeys.PaymentAccount,
                                        payAccountValues[index],
                                    );
                                }}
                                extraInfo={strings.paymentExtra}
                                required={true}
                                validationMessage={formValidation[AppKeys.PaymentAccount]}
                                onBlur={onBlurInput}
                            />
                        </div>
                    </Column>
                    <Column large={5} medium={4}>
                        <div className={styles.rightInputColumn}>
                            <div className={styles.cardContainerDesktop}>
                                {renderCardPicker
                                    ? renderCardPicker()
                                    : renderCard(
                                          application[AppKeys.FirstName],
                                          application[AppKeys.LastName],
                                          defaultCardBrand,
                                          singleCardType,
                                      )}
                            </div>
                            <MoneyInput
                                id={AppKeys.AccountTotal}
                                label={strings.accountTotal}
                                name={AppKeys.AccountTotal}
                                ref={inputRefs.current[AppKeys.AccountTotal]}
                                onBlur={onBlurInput}
                                value={application[AppKeys.AccountTotal]}
                                setValue={(value) => {
                                    onChangeApplication(AppKeys.AccountTotal, value);
                                }}
                                extraInfo={strings.accountTotalExtra}
                                required={true}
                                validationMessage={formValidation[AppKeys.AccountTotal]}
                                allowDecimals={false}
                                max={99999999}
                                inputMode="numeric"
                            />
                            <MoneyInput
                                id={AppKeys.AnnualGrossIncome}
                                name={AppKeys.AnnualGrossIncome}
                                label={strings.annualGrossIncome}
                                ref={inputRefs.current[AppKeys.AnnualGrossIncome]}
                                value={application[AppKeys.AnnualGrossIncome]}
                                setValue={(value) => {
                                    onChangeApplication(AppKeys.AnnualGrossIncome, value);
                                }}
                                extraInfo={strings.annualGrossIncomeExtra}
                                onBlur={(e) => {
                                    e.preventDefault();
                                    if (
                                        application[AppKeys.AnnualGrossIncome] === "$0" &&
                                        !wasGrossIncomeModalShown
                                    ) {
                                        openGrossIncomeModal();
                                    } else if (wasGrossIncomeModalShown) {
                                        setWasGrossIncomeModalShown(false);
                                    }
                                    if (onBlurInput) {
                                        onBlurInput(e);
                                    }
                                }}
                                required={true}
                                max={99999999}
                                validationMessage={formValidation[AppKeys.AnnualGrossIncome]}
                                inputMode="numeric"
                            />
                            <Select
                                id={AppKeys.Residency}
                                name={AppKeys.Residency}
                                label={strings.residency}
                                ref={selectRefs.current[AppKeys.Residency]}
                                options={boolDecisionStrings}
                                onBlur={onBlurInput}
                                selectedIndex={getResidencyIndex()}
                                setSelected={(index) => {
                                    const value = boolDecisionArr[index];
                                    const residency = value === BoolDecision.Yes;
                                    onChangeApplication(AppKeys.Residency, residency);
                                }}
                                required={true}
                                validationMessage={formValidation[AppKeys.Residency]}
                            />
                            <div className={styles.checkboxContainer}>
                                <ExpandingCheckbox
                                    className={styles.checkbox}
                                    id={`${AppKeys.AnnualObligation}_checkbox`}
                                    label={strings.monthlyPayment}
                                    isChecked={checkbox}
                                    ref={monthlyhCheckboxRef}
                                    onChange={(e) => {
                                        setCheckbox(e.target.checked);
                                        if (e.target.checked) {
                                            inputRefs.current[
                                                AppKeys.AnnualObligation
                                            ]?.current?.focus();
                                        } else {
                                            // Clear value if no longer checked
                                            onChangeApplication(AppKeys.AnnualObligation, "");
                                        }
                                        const value = e.target.checked ? "true" : "false";
                                        callApplicationLead(
                                            `${AppKeys.AnnualObligation}-checkbox`,
                                            application[AppKeys.InviteCode],
                                            windowUrl,
                                            value,
                                        );
                                    }}
                                >
                                    {(inputProps) => {
                                        return (
                                            <MoneyInput
                                                {...inputProps}
                                                id={AppKeys.AnnualObligation}
                                                label={strings.monthlyObligation}
                                                name={AppKeys.AnnualObligation}
                                                ref={inputRefs.current[AppKeys.AnnualObligation]}
                                                className={styles.monthlyObligation}
                                                onBlur={(e) => {
                                                    e.preventDefault();
                                                    if (
                                                        application[AppKeys.AnnualObligation] ===
                                                            "$0" &&
                                                        !wasMonthlyObligationsModalShown
                                                    ) {
                                                        openMonthlyObligationsModal();
                                                    } else if (wasMonthlyObligationsModalShown) {
                                                        setWasMonthlyObligationsModalShown(false);
                                                    }
                                                    if (onBlurInput) {
                                                        onBlurInput(e);
                                                    }
                                                }}
                                                setValue={(value) => {
                                                    onChangeApplication(
                                                        AppKeys.AnnualObligation,
                                                        value,
                                                    );
                                                }}
                                                value={application[AppKeys.AnnualObligation]}
                                                extraInfo={strings.monthlyPaymentExtra}
                                                required={checkbox}
                                                validationMessage={
                                                    formValidation[AppKeys.AnnualObligation]
                                                }
                                                allowDecimals={false}
                                                max={99999999}
                                                inputMode="numeric"
                                            />
                                        );
                                    }}
                                </ExpandingCheckbox>
                            </div>
                            {!renderAgreement && !renderPaperless ? (
                                <>
                                    {validation &&
                                    validation.generic !== null &&
                                    validation.generic !== undefined &&
                                    validation.generic.length > 0 ? (
                                        <ValidationList messages={validation.generic} />
                                    ) : null}
                                    <div className={styles.footer}>
                                        <ActionButton
                                            ref={continueBtnRef}
                                            label={continueLabel}
                                            loading={loading}
                                            disabled={continueDisabled}
                                        />
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </Column>
                    <Modal
                        id="grossIncomeModal"
                        label={strings.grossIncomeModal}
                        isOpen={grossIncomeModal}
                        onClose={closeGrossIncomeModal}
                        onSubmit={closeGrossIncomeModal}
                        onSubmitLabel={strings.continue}
                        triggerElement={inputRefs.current[AppKeys.AnnualGrossIncome]?.current}
                    >
                        <div className={styles.modalText}>{strings.grossIncomeModalText}</div>
                    </Modal>
                    <Modal
                        id="monthlyObligationsModal"
                        label={strings.monthlyObligationsModal}
                        isOpen={monthlyObligationsModal}
                        onClose={closeMonthlyObligationsModal}
                        onSubmit={closeMonthlyObligationsModal}
                        onSubmitLabel={strings.continue}
                        triggerElement={monthlyhCheckboxRef.current}
                    >
                        <div className={styles.modalText}>
                            {strings.monthlyObligationsModalText}
                        </div>
                    </Modal>
                </Row>
                {renderAgreement || renderPaperless ? (
                    <Row>
                        <Column largeOffset={1} large={10} medium={8}>
                            <div className={styles.agreementContainer}>
                                {renderAgreement ? renderAgreement() : null}

                                {renderPaperless ? renderPaperless() : null}
                            </div>

                            {validation &&
                            validation.generic !== null &&
                            validation.generic !== undefined &&
                            validation.generic.length > 0 ? (
                                <ValidationList messages={validation.generic} />
                            ) : null}

                            <Column largeOffset={5} mediumOffset={4} large={5} medium={4}>
                                <div className={styles.checkboxFooter}>
                                    <ActionButton
                                        ref={continueBtnRef}
                                        label={continueLabel}
                                        loading={loading}
                                    />
                                </div>
                            </Column>
                        </Column>
                    </Row>
                ) : null}
            </Container>
        </form>
    );
}
